import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, Input } from '@mlm/ui';
import { listPosProducts } from '../lib/api';
import { useActiveBranch } from '../lib/use-branch';
import { BranchPicker } from '../components/branch-picker';

/**
 * Inventory tab (feature 13) — current stock of the branch. Reuses the same
 * branch-aware product catalog the checkout grid reads (`/pos/products`); this
 * is the read-only stock view with a name/SKU filter and a low-stock highlight.
 */
export function InventoryPage() {
  const { branchId, setSelectedBranchId, isScoped, branches } = useActiveBranch();
  const [filter, setFilter] = useState('');

  const query = useQuery({
    queryKey: ['pos-inventory', branchId],
    enabled: !!branchId,
    queryFn: () => listPosProducts(branchId!),
  });

  const rows = (query.data ?? []).filter((p) => {
    const f = filter.trim().toLowerCase();
    if (!f) return true;
    return p.name.toLowerCase().includes(f) || p.sku.toLowerCase().includes(f);
  });

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Branch inventory</CardTitle>
        <BranchPicker branches={branches} branchId={branchId} isScoped={isScoped} onChange={setSelectedBranchId} />
      </CardHeader>
      <CardContent className="space-y-4">
        <Input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by name or SKU…"
          className="max-w-xs"
        />
        {query.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : !rows.length ? (
          <p className="text-sm text-muted-foreground">No products match.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-muted-foreground border-b">
              <tr>
                <th className="px-2 py-1.5">SKU</th>
                <th className="px-2 py-1.5">Product</th>
                <th className="px-2 py-1.5 text-right">Price</th>
                <th className="px-2 py-1.5 text-right">On hand</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} className="border-b last:border-0 hover:bg-muted/30">
                  <td className="px-2 py-1.5 font-mono text-xs">{p.sku}</td>
                  <td className="px-2 py-1.5">{p.name}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    ₱{Number(p.effectivePrice).toFixed(2)}
                  </td>
                  <td
                    className={`px-2 py-1.5 text-right tabular-nums font-medium ${
                      p.qty <= 0 ? 'text-destructive' : p.qty < 5 ? 'text-warning' : ''
                    }`}
                  >
                    {p.qty}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}
